import { cn } from '@reflex/ui/lib/utils';

/**
 * Chief's brand mark, drawn in the app's icon language (the lucide grammar
 * the Launch buttons use): a single `currentColor` stroke outline on the
 * 24px grid — no fills, no gradients, no shading. The one piece of flair
 * is the visor, a single flat color chip: amber standing by, Halo shield
 * blue when replies are waiting.
 *
 * Because the shell is `currentColor` it sits anywhere monochrome icons
 * do; callers that want Chief's own ink instead of the surrounding text
 * color pin the `CHIEF_INK_*` classes that live beside Chief's own surfaces
 * in `@reflex/plugin-chief`.
 *
 * The glyph itself lives in the design system rather than in the plugin
 * because Chief now appears outside its own surfaces: a chat bubble Chief
 * relayed shows this mark next to the user it was sent for.
 */
const VISOR_AMBER = '#e8a33d';
const VISOR_SHIELD = '#38bdf8';

export type ChiefVisorTone = 'amber' | 'shield';

interface ChiefVisorIconProps {
  className?: string;
  /** Visor color: `amber` standing by, `shield` when something is waiting. */
  visor?: ChiefVisorTone;
}

export function ChiefVisorIcon({ className, visor = 'amber' }: ChiefVisorIconProps) {
  const visorFill = visor === 'shield' ? VISOR_SHIELD : VISOR_AMBER;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="Chief"
      className={cn('size-5', className)}
    >
      {/* Helmet outline: dome into the tapered jaw, one continuous shape. */}
      <path d="M4 10.2C4 5.7 7.6 2.5 12 2.5s8 3.2 8 7.7v4a3.2 3.2 0 0 1-.94 2.26l-3.1 3.1a3.2 3.2 0 0 1-2.26.94h-3.4a3.2 3.2 0 0 1-2.26-.94l-3.1-3.1A3.2 3.2 0 0 1 4 14.2z" />
      {/* Brow line above the visor. */}
      <path d="M4.6 8.4h14.8" />
      {/* The one piece of flair: the visor, a single flat color. */}
      <path
        d="M7.1 11h9.8l-.37 2c-.12.67-.65 1.2-1.32 1.31l-2.63.46c-.38.07-.78.07-1.16 0l-2.63-.46a1.66 1.66 0 0 1-1.32-1.31z"
        fill={visorFill}
        stroke="none"
      />
    </svg>
  );
}
